// SimpleMarkdown — a lightweight markdown renderer for the Help system.
// No external dependencies. Handles the subset of markdown used in help content:
//   Headings (h1/h2/h3), bold, italic, inline code, fenced code blocks,
//   unordered lists, ordered lists, blockquotes (with callout variants), hr, paragraphs.

import React from 'react'

// ── Inline formatting ──────────────────────────────────────────────────────

function InlineText({ text }) {
  if (!text) return null
  const parts = []
  // Pattern matches **bold**, *italic*, `code`
  const pattern = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g
  let last = 0
  let match

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index))
    }
    const token = match[0]
    if (token.startsWith('**')) {
      parts.push(<strong key={match.index}>{token.slice(2, -2)}</strong>)
    } else if (token.startsWith('*')) {
      parts.push(<em key={match.index}>{token.slice(1, -1)}</em>)
    } else {
      parts.push(
        <code key={match.index} className="md-inline-code">
          {token.slice(1, -1)}
        </code>
      )
    }
    last = match.index + token.length
  }

  if (last < text.length) parts.push(text.slice(last))
  return <>{parts}</>
}

// ── Blockquote callout detection ──────────────────────────────────────────

function blockquoteClass(lines) {
  const first = lines[0] || ''
  if (first.startsWith('**Note:') || first.startsWith('**Note.**')) return 'md-callout md-callout-note'
  if (first.startsWith('**Warning:') || first.startsWith('**Warning.**')) return 'md-callout md-callout-warn'
  if (first.startsWith('**Tip:') || first.startsWith('**Tip.**') || first.startsWith('**Ready')) return 'md-callout md-callout-tip'
  return 'md-blockquote'
}

// ── Main parser ────────────────────────────────────────────────────────────

function parseMarkdown(md) {
  const lines = (md || '').split('\n')
  const elements = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // ── Fenced code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim()
      const codeLines = []
      i++
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }
      elements.push(
        <pre key={`cb-${i}`} className={`md-code-block${lang ? ` lang-${lang}` : ''}`}>
          <code>{codeLines.join('\n')}</code>
        </pre>
      )
      i++ // skip closing ```
      continue
    }

    // ── Headings
    if (line.startsWith('### ')) {
      elements.push(<h3 key={`h3-${i}`} className="md-h3"><InlineText text={line.slice(4)} /></h3>)
      i++; continue
    }
    if (line.startsWith('## ')) {
      elements.push(<h2 key={`h2-${i}`} className="md-h2"><InlineText text={line.slice(3)} /></h2>)
      i++; continue
    }
    if (line.startsWith('# ')) {
      elements.push(<h1 key={`h1-${i}`} className="md-h1"><InlineText text={line.slice(2)} /></h1>)
      i++; continue
    }

    // ── Horizontal rule
    if (/^-{3,}$/.test(line.trim())) {
      elements.push(<hr key={`hr-${i}`} className="md-hr" />)
      i++; continue
    }

    // ── Blockquote / callout
    if (line.startsWith('> ')) {
      const bqLines = []
      while (i < lines.length && lines[i].startsWith('> ')) {
        bqLines.push(lines[i].slice(2))
        i++
      }
      const cls = blockquoteClass(bqLines)
      elements.push(
        <blockquote key={`bq-${i}`} className={cls}>
          {bqLines.map((l, idx) => (
            <p key={idx}><InlineText text={l} /></p>
          ))}
        </blockquote>
      )
      continue
    }

    // ── Unordered list
    if (/^[-*] /.test(line)) {
      const items = []
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(lines[i].slice(2))
        i++
      }
      elements.push(
        <ul key={`ul-${i}`} className="md-ul">
          {items.map((item, idx) => (
            <li key={idx}><InlineText text={item} /></li>
          ))}
        </ul>
      )
      continue
    }

    // ── Ordered list
    if (/^\d+\. /.test(line)) {
      const items = []
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''))
        i++
      }
      elements.push(
        <ol key={`ol-${i}`} className="md-ol">
          {items.map((item, idx) => (
            <li key={idx}><InlineText text={item} /></li>
          ))}
        </ol>
      )
      continue
    }

    // ── Empty line
    if (line.trim() === '') {
      i++; continue
    }

    // ── Paragraph — collect consecutive non-structural lines
    const pLines = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('>') &&
      !lines[i].startsWith('```') &&
      !/^[-*] /.test(lines[i]) &&
      !/^\d+\. /.test(lines[i]) &&
      !/^-{3,}$/.test(lines[i].trim())
    ) {
      pLines.push(lines[i])
      i++
    }

    if (pLines.length > 0) {
      elements.push(
        <p key={`p-${i}`} className="md-p">
          <InlineText text={pLines.join(' ')} />
        </p>
      )
    }
  }

  return elements
}

// ── Component ──────────────────────────────────────────────────────────────

export default function SimpleMarkdown({ content, className }) {
  const elements = React.useMemo(() => parseMarkdown(content), [content])
  return (
    <div className={`md-content${className ? ` ${className}` : ''}`}>
      {elements}
    </div>
  )
}
